import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "./usePagination";

export type UseCursorPaginationResult = {
  /** 1-based page index for display. */
  page: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  /** Cursor to send to the API (`undefined` = first page). */
  cursor: string | undefined;
  /** Call after a successful fetch with the response `nextCursor`. */
  setNextCursor: (next: string | undefined) => void;
  hasMore: boolean;
  canPrev: boolean;
  canNext: boolean;
  goNext: () => void;
  goPrev: () => void;
  goFirst: () => void;
  /** Reset to page 1 (clears cursor stack). */
  reset: () => void;
  /** Approximate "showing" range for the current page (no total). */
  showingFrom: number;
  showingTo: (itemCount: number) => number;
};

/**
 * DynamoDB-style cursor pagination with a prev-stack.
 * Pair with `?limit=&cursor=` list APIs that return `{ hasMore, nextCursor }`.
 */
export function useCursorPagination(
  resetDeps: unknown[] = [],
  options?: { initialPageSize?: number },
): UseCursorPaginationResult {
  const [pageSize, setPageSizeState] = useState(
    options?.initialPageSize ?? DEFAULT_PAGE_SIZE,
  );
  const [page, setPage] = useState(1);
  /** Cursors used to *enter* each page. Index 0 is always undefined (page 1). */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [nextCursor, setNextCursorState] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const reset = useCallback(() => {
    setPage(1);
    setCursorStack([undefined]);
    setNextCursorState(undefined);
    setHasMore(false);
  }, []);

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on explicit deps only
  }, [pageSize, ...resetDeps]);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
  }, []);

  const setNextCursor = useCallback((next: string | undefined) => {
    setNextCursorState(next);
    setHasMore(Boolean(next));
  }, []);

  const goNext = useCallback(() => {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, nextCursor]);
    setPage((p) => p + 1);
    setNextCursorState(undefined);
    setHasMore(false);
  }, [nextCursor]);

  const goPrev = useCallback(() => {
    setCursorStack((stack) => {
      if (stack.length <= 1) return stack;
      return stack.slice(0, -1);
    });
    setPage((p) => Math.max(1, p - 1));
    setNextCursorState(undefined);
    setHasMore(false);
  }, []);

  const goFirst = useCallback(() => {
    reset();
  }, [reset]);

  const cursor = cursorStack[cursorStack.length - 1];
  const showingFrom = (page - 1) * pageSize + 1;

  return {
    page,
    pageSize,
    setPageSize,
    cursor,
    setNextCursor,
    hasMore,
    canPrev: page > 1,
    canNext: hasMore,
    goNext,
    goPrev,
    goFirst,
    reset,
    showingFrom,
    showingTo: (itemCount: number) =>
      itemCount === 0 ? 0 : showingFrom + itemCount - 1,
  };
}
